import { useState } from "react";
import type { PackageRow, VersionRow } from "../hooks/usePackages.js";
import {
  usePatchPackage,
  usePatchVersion,
} from "../hooks/usePackages.js";
import { getCurrentAdmin } from "../lib/auth.js";

/**
 * 编辑已上架 skill 的展示字段:
 *   - displayName / description / tags / changelog (版本级)
 *   - channel (包级,owner 独占)
 *
 * 不改的:包名 / version / zip / sha256 (这些改动会破坏客户端缓存)
 * 改内容需要发新版本,本 Modal 不做。
 */
export function EditSkillModal({
  pkg,
  latest,
  onClose,
}: {
  pkg: PackageRow;
  latest: VersionRow;
  onClose: () => void;
}) {
  const me = getCurrentAdmin();
  const patchVersion = usePatchVersion();
  const patchPackage = usePatchPackage();

  const initialTags = latest.tags_json
    ? (JSON.parse(latest.tags_json) as string[]).join(", ")
    : "";

  const [displayName, setDisplayName] = useState(latest.display_name ?? "");
  const [description, setDescription] = useState(latest.description ?? "");
  const [tagsInput, setTagsInput] = useState(initialTags);
  const [changelog, setChangelog] = useState(latest.changelog ?? "");
  const [channel, setChannel] = useState<PackageRow["channel"]>(pkg.channel);
  const [err, setErr] = useState<string | null>(null);

  const busy = patchVersion.isPending || patchPackage.isPending;

  const handleSave = async () => {
    setErr(null);
    try {
      const tags = tagsInput
        .split(/[,，、\s]+/)
        .map((t) => t.trim())
        .filter(Boolean);

      // 版本级字段 patch
      await patchVersion.mutateAsync({
        name: pkg.name,
        version: latest.version,
        patch: {
          displayName: displayName.trim() || null,
          description: description.trim() || null,
          tags: tags.length > 0 ? tags : null,
          changelog: changelog.trim() || null,
        },
      });

      // 包级 channel 单独走(owner 独占)
      if (channel !== pkg.channel) {
        if (me?.role !== "owner") {
          setErr("修改频道需要 owner 权限");
          return;
        }
        await patchPackage.mutateAsync({ name: pkg.name, channel });
      }

      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="modal-mask" onClick={busy ? undefined : onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          width: 520,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div>
          <h2 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>
            编辑元数据
          </h2>
          <p className="page-sub" style={{ marginTop: 4 }}>
            <code className="code-soft">{pkg.name}</code> · v{latest.version} —
            仅改展示字段,不动 zip 和版本号
          </p>
        </div>

        <label style={styles.label}>
          <span>显示名称</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="客户端列表和详情展示的主标题(中文友好)"
            disabled={busy}
          />
        </label>

        <label style={styles.label}>
          <span>描述</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="一句话简短描述 skill 的用途(会在卡片里显示两行)"
            rows={3}
            disabled={busy}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </label>

        <label style={styles.label}>
          <span>标签(多个用逗号/空格分隔)</span>
          <input
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="办公, 文档, 表格"
            disabled={busy}
          />
        </label>

        <label style={styles.label}>
          <span>Changelog(本版本的变更说明)</span>
          <textarea
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            placeholder="例如:修复导出 xlsx 合并单元格的样式丢失问题"
            rows={2}
            disabled={busy}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </label>

        <label style={styles.label}>
          <span>频道{me?.role !== "owner" && "(需 owner 权限)"}</span>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as PackageRow["channel"])}
            disabled={busy || me?.role !== "owner"}
          >
            <option value="official">官方</option>
            <option value="community">社区</option>
            <option value="private">企业私有</option>
          </select>
        </label>

        {err && <div className="err">{err}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 4, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={busy}
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  label: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    fontSize: 12,
    fontWeight: 500,
    color: "var(--text-muted)",
  },
};
